import React, { useState, useEffect } from "react";
import { Briefcase, ChevronDown, User, ShieldCheck, LogOut, X, Menu, Edit2, Save } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useMsal } from "@azure/msal-react";
import { Modal, message } from "antd";
import "../Styles/Header.scss";

// Comprehensive Azure AD B2C Dynamic Identity Extractor
const extractUserIdentity = (accounts, instance = null) => {
  let email = "";
  let name = "";
  let org = "";
  let tier = "";
  let role = "";

  const activeAccount =
    (instance && instance.getActiveAccount && instance.getActiveAccount()) ||
    (accounts && accounts.length > 0 ? accounts[0] : null);

  if (activeAccount) {
    const claims = activeAccount.idTokenClaims || {};
    console.log("🔍 [Azure AD B2C Claims]:", claims, "Account:", activeAccount);

    // 1. Dynamic Check across all token claims & account properties for Email
    if (Array.isArray(claims.emails) && claims.emails.length > 0 && claims.emails[0]) {
      email = claims.emails[0];
    } else if (typeof claims.email === "string" && claims.email.includes("@")) {
      email = claims.email;
    } else if (claims.signInNames && typeof claims.signInNames.emailAddress === "string") {
      email = claims.signInNames.emailAddress;
    } else if (typeof claims.preferred_username === "string" && claims.preferred_username.includes("@")) {
      email = claims.preferred_username;
    } else if (typeof claims.upn === "string" && claims.upn.includes("@")) {
      email = claims.upn;
    } else if (typeof claims.unique_name === "string" && claims.unique_name.includes("@")) {
      email = claims.unique_name;
    } else if (Array.isArray(claims.otherMails) && claims.otherMails.length > 0) {
      email = claims.otherMails[0];
    } else if (typeof activeAccount.username === "string" && activeAccount.username.includes("@")) {
      email = activeAccount.username;
    } else {
      for (const key of Object.keys(claims)) {
        if (typeof claims[key] === "string" && claims[key].includes("@") && !claims[key].startsWith("http")) {
          email = claims[key];
          break;
        }
      }
    }

    // 2. Discover Name dynamically from claims
    name =
      claims.name ||
      claims.given_name ||
      (claims.given_name && claims.family_name ? `${claims.given_name} ${claims.family_name}` : null) ||
      claims.extension_DisplayName ||
      claims.extension_FullName ||
      activeAccount.name ||
      "";

    // 3. Organization / Company from B2C
    org =
      claims.extension_Company ||
      claims.extension_CompanyName ||
      claims.extension_Organization ||
      claims.company ||
      claims.companyName ||
      "";

    // 4. Partner Tier from B2C
    tier =
      claims.extension_Tier ||
      claims.extension_PartnerTier ||
      claims.tier ||
      "";

    // 5. Role from B2C
    role =
      claims.extension_Role ||
      (Array.isArray(claims.roles) && claims.roles[0]) ||
      claims.role ||
      claims.jobTitle ||
      "";
  }

  // Fallbacks to storage if claims were not present in token
  if (typeof window !== "undefined") {
    if (!email) {
      email = sessionStorage.getItem("user_email") || localStorage.getItem("user_email") || "";
    }
    if (!name) {
      name = sessionStorage.getItem("user_name") || localStorage.getItem("user_name") || "";
    }
    if (!org) {
      org = sessionStorage.getItem("company_name") || localStorage.getItem("company_name") || localStorage.getItem("partner_org_name") || "";
    }
    if (!tier) {
      tier = localStorage.getItem("partner_tier") || "";
    }
    if (!role) {
      role = localStorage.getItem("partner_role") || "";
    }
  }

  // Auto-derive clean name from email if name was not provided
  if (!name && email && email.includes("@")) {
    name = email
      .split("@")[0]
      .replace(/[._+-]/g, " ")
      .replace(/\b\w/g, (c) => c.toUpperCase());
  }

  return {
    email: email || "",
    name: name || (email ? email.split("@")[0] : "Partner User"),
    org: org || "iNextLabs Enterprise",
    tier: tier || "Platinum Partner",
    role: role || "Partner Admin",
  };
};

const getInitials = (name, email) => {
  if (name && name !== "Partner Center" && name !== "Partner User") {
    const parts = name.trim().split(/\s+/);
    if (parts.length >= 2 && parts[0] && parts[1]) {
      return (parts[0][0] + parts[1][0]).toUpperCase();
    }
    if (parts[0].length >= 2) {
      return parts[0].slice(0, 2).toUpperCase();
    }
    return parts[0][0].toUpperCase();
  }
  if (email && email.includes("@")) {
    const prefix = email.split("@")[0].replace(/[^a-zA-Z]/g, "");
    if (prefix.length >= 2) return prefix.slice(0, 2).toUpperCase();
    if (prefix.length === 1) return prefix[0].toUpperCase();
  }
  return "IN";
};

const headerProducts = [
  { name: "EngageAI", label: "EngageAI" },
  { name: "DocsAI", label: "DocsAI" },
  { name: "InsightsAI", label: "InsightsAI" },
  { name: "WhatsApp", label: "WhatsApp" },
];

const Header = ({ selectedProduct = "EngageAI", onOpenAssets, onSelectProduct }) => {
  const navigate = useNavigate();
  const { instance, accounts, inProgress } = useMsal();

  const [scrolled, setScrolled] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showAccountModal, setShowAccountModal] = useState(false);

  // Dynamic Interactive Account Settings States
  const [userEmail, setUserEmail] = useState(() => extractUserIdentity(accounts, instance).email);
  const [userName, setUserName] = useState(() => extractUserIdentity(accounts, instance).name);
  const [orgName, setOrgName] = useState(() => extractUserIdentity(accounts, instance).org);
  const [partnerTier, setPartnerTier] = useState(() => extractUserIdentity(accounts, instance).tier);
  const [partnerRole, setPartnerRole] = useState(() => extractUserIdentity(accounts, instance).role);

  const [isEditingName, setIsEditingName] = useState(false);
  const [tempName, setTempName] = useState(userName);

  const [isEditingEmail, setIsEditingEmail] = useState(false);
  const [tempEmail, setTempEmail] = useState(userEmail);

  const [isEditingOrg, setIsEditingOrg] = useState(false);
  const [tempOrgName, setTempOrgName] = useState(orgName);

  // Reactive listener for MSAL account changes & storage sync
  useEffect(() => {
    const identity = extractUserIdentity(accounts, instance);
    if (identity.email || identity.name) {
      setUserEmail(identity.email);
      setUserName(identity.name);
      setOrgName(identity.org);
      setPartnerTier(identity.tier);
      setPartnerRole(identity.role);
      setTempName(identity.name);
      setTempEmail(identity.email);
      setTempOrgName(identity.org);

      if (identity.email) {
        sessionStorage.setItem("user_email", identity.email);
        localStorage.setItem("user_email", identity.email);
      }
      if (identity.name) {
        sessionStorage.setItem("user_name", identity.name);
        localStorage.setItem("user_name", identity.name);
      }
      if (identity.org) {
        localStorage.setItem("partner_org_name", identity.org);
      }
    }
  }, [accounts, inProgress, instance]);

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 20) {
        setScrolled(true);
      } else {
        setScrolled(false);
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (!e.target.closest(".partner-nav-user") && !e.target.closest(".partner-popover-user-menu")) {
        setShowUserMenu(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleLogout = () => {
    sessionStorage.clear();
    localStorage.clear();

    if (instance && accounts && accounts.length > 0) {
      instance.logoutRedirect({
        postLogoutRedirectUri: window.location.origin + "/login",
      });
    } else {
      navigate("/login");
    }
  };

  const handleAssetsClick = () => {
    if (onOpenAssets) {
      onOpenAssets();
    } else {
      const resourcesEl = document.getElementById("section-resources");
      if (resourcesEl) {
        resourcesEl.scrollIntoView({ behavior: "smooth" });
      }
    }
  };

  const handleSaveName = () => {
    if (!tempName.trim()) {
      message.error("Name cannot be empty.");
      return;
    }
    setUserName(tempName.trim());
    sessionStorage.setItem("user_name", tempName.trim());
    localStorage.setItem("user_name", tempName.trim());
    setIsEditingName(false);
    message.success("Name updated successfully!");
  };

  const handleSaveEmail = () => {
    if (!tempEmail.trim()) {
      message.error("Email cannot be empty.");
      return;
    }
    setUserEmail(tempEmail.trim());
    sessionStorage.setItem("user_email", tempEmail.trim());
    localStorage.setItem("user_email", tempEmail.trim());
    setIsEditingEmail(false);
    message.success("Email address updated successfully!");
  };

  const handleSaveOrgName = () => {
    if (!tempOrgName.trim()) {
      message.error("Organization name cannot be empty.");
      return;
    }
    setOrgName(tempOrgName.trim());
    localStorage.setItem("partner_org_name", tempOrgName.trim());
    sessionStorage.setItem("company_name", tempOrgName.trim());
    setIsEditingOrg(false);
    message.success("Organization name updated!");
  };

  return (
    <header className={`partner-navbar-header ${scrolled ? "scrolled" : ""}`}>
      <div className="partner-navbar-container">
        {/* Left Brand Logo & Status Tag */}
        <div className="partner-navbar-brand">
          <div className="partner-navbar-logo" onClick={() => navigate("/")}>
            <img
              src="/images/brand/inextlabs-logo.png"
              alt="iNextLabs Logo"
              className="partner-header-logo-img"
            />
          </div>
          <span className="partner-brand-divider" />
          <div className="partner-brand-tag-wrapper">
            <span className="partner-brand-status-dot" />
            <span className="partner-brand-tag">Partner Center</span>
          </div>
        </div>

        {/* Center Spatial Product Navigation (Desktop >= 1024px) */}
        <div className="partner-header-center-nav">
          {headerProducts.map((prod) => {
            const isActive =
              selectedProduct &&
              selectedProduct.toLowerCase() === prod.name.toLowerCase();
            return (
              <button
                key={prod.name}
                type="button"
                className={`partner-center-nav-link ${isActive ? "active" : ""}`}
                onClick={() => {
                  if (onSelectProduct) {
                    onSelectProduct(prod.name, true);
                  } else {
                    navigate(`/${prod.name.toLowerCase()}`);
                    const el = document.getElementById("section-overview");
                    if (el) el.scrollIntoView({ behavior: "smooth" });
                  }
                }}
              >
                {prod.label}
              </button>
            );
          })}
        </div>

        {/* Right Spatial Nav Actions */}
        <div className="partner-navbar-actions">
          {/* Assets Button */}
          <button
            type="button"
            className="partner-nav-btn partner-nav-assets-pill"
            onClick={handleAssetsClick}
          >
            <Briefcase size={15} className="partner-nav-icon" />
            <span>Assets</span>
            <span className="partner-nav-sparkle-dot" />
          </button>

          {/* User Profile Dropdown Pill */}
          <div className="partner-popover-wrapper">
            <div
              className={`partner-nav-user ${showUserMenu ? "active" : ""}`}
              onClick={() => {
                setShowUserMenu(!showUserMenu);
              }}
            >
              <div className="partner-user-monogram-badge">
                <span>{getInitials(userName, userEmail)}</span>
              </div>
              <div className="partner-user-info">
                <span className="partner-user-name">
                  {userName}
                  <ChevronDown size={12} className={`partner-user-chevron ${showUserMenu ? "rotate" : ""}`} />
                </span>
                <span className="partner-user-role">{partnerRole}</span>
              </div>
            </div>

            {showUserMenu && (
              <div className="partner-popover-user-menu apple-glass-popover">
                <div className="partner-user-menu-header">
                  <div className="partner-user-monogram-badge large">
                    <span>{getInitials(userName, userEmail)}</span>
                  </div>
                  <div className="partner-user-menu-details">
                    <span className="partner-user-menu-name">{userName}</span>
                    {userEmail ? (
                      <span className="partner-user-menu-email">{userEmail}</span>
                    ) : (
                      <span className="partner-user-menu-email" style={{ opacity: 0.65, fontSize: "11.5px" }}>Authorized Partner</span>
                    )}
                    <span className="partner-user-menu-tier">
                      <ShieldCheck size={12} color="#10b981" />
                      {partnerTier}
                    </span>
                  </div>
                </div>

                <div className="partner-user-menu-divider" />

                <div className="partner-user-menu-items">
                  <button
                    type="button"
                    className="partner-menu-item"
                    onClick={() => {
                      setShowAccountModal(true);
                      setShowUserMenu(false);
                    }}
                  >
                    <User size={15} />
                    <span>Account Settings</span>
                  </button>
                  <button type="button" className="partner-menu-item danger" onClick={handleLogout}>
                    <LogOut size={15} />
                    <span>Log Out</span>
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Apple Mobile Spatial Menu Trigger */}
          <button
            type="button"
            className="partner-mobile-menu-trigger"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label="Toggle Navigation"
          >
            {mobileMenuOpen ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
      </div>

      {/* Apple Mobile Spatial Glass Full Sheet */}
      {mobileMenuOpen && (
        <div className="partner-apple-mobile-sheet">
          <div className="partner-mobile-sheet-content">
            <div className="partner-mobile-user-card">
              <img
                src="https://images.unsplash.com/photo-1534528741775-53994a69daeb?auto=format&fit=crop&w=100&q=80"
                alt={userName}
              />
              <div>
                <h3>{userName}</h3>
                <span className="partner-tier-tag">
                  <ShieldCheck size={12} color="#10b981" /> {partnerTier}
                </span>
              </div>
            </div>

            {/* Mobile Product Selector Grid */}
            <div className="partner-mobile-product-grid" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px", marginBottom: "16px" }}>
              {headerProducts.map((prod) => {
                const isActive = selectedProduct && selectedProduct.toLowerCase() === prod.name.toLowerCase();
                return (
                  <button
                    key={prod.name}
                    type="button"
                    className={`mobile-sheet-btn ${isActive ? "active" : ""}`}
                    style={{
                      padding: "10px 14px",
                      borderRadius: "12px",
                      background: isActive ? "rgba(240, 87, 66, 0.12)" : "rgba(0, 0, 0, 0.04)",
                      color: isActive ? "#f05742" : "#1d1d1f",
                      fontWeight: isActive ? "650" : "500",
                      border: isActive ? "1px solid rgba(240, 87, 66, 0.35)" : "1px solid transparent",
                      cursor: "pointer",
                      textAlign: "center"
                    }}
                    onClick={() => {
                      if (onSelectProduct) {
                        onSelectProduct(prod.name, true);
                      } else {
                        navigate(`/${prod.name.toLowerCase()}`);
                        const el = document.getElementById("section-overview");
                        if (el) el.scrollIntoView({ behavior: "smooth" });
                      }
                      setMobileMenuOpen(false);
                    }}
                  >
                    <span>{prod.label}</span>
                  </button>
                );
              })}
            </div>

            <div className="partner-mobile-sheet-grid">
              <button
                type="button"
                className="mobile-sheet-btn"
                onClick={() => {
                  handleAssetsClick();
                  setMobileMenuOpen(false);
                }}
              >
                <Briefcase size={16} color="#f05742" />
                <span>Partner Assets</span>
              </button>
              <button
                type="button"
                className="mobile-sheet-btn"
                onClick={() => {
                  setShowAccountModal(true);
                  setMobileMenuOpen(false);
                }}
              >
                <User size={16} color="#3b82f6" />
                <span>Account Settings</span>
              </button>
            </div>

            <div className="partner-mobile-actions-stack">
              <button
                type="button"
                className="mobile-action-btn danger"
                onClick={handleLogout}
              >
                <LogOut size={16} />
                <span>Log Out</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Account Settings Apple Spatial Modal */}
      <Modal
        open={showAccountModal}
        onCancel={() => setShowAccountModal(false)}
        footer={null}
        width={560}
        closeIcon={
          <div className="apple-drawer-close-pill">
            <X size={16} />
          </div>
        }
        className="partner-apple-account-modal"
      >
        <div className="partner-modal-account-content">
          <div className="partner-modal-header">
            <div className="partner-modal-avatar-ring">
              <img
                src="https://images.unsplash.com/photo-1534528741775-53994a69daeb?auto=format&fit=crop&w=100&q=80"
                alt={userName}
              />
            </div>
            <div className="partner-modal-user-meta">
              <h2>{userName}</h2>
              <span className="partner-modal-role-badge">{partnerRole}</span>
              <p className="partner-modal-email">{userEmail}</p>
            </div>
          </div>

          <div className="partner-modal-grid">
            {/* Display Name Card */}
            <div className="partner-modal-card">
              <div className="card-label-row">
                <span className="card-label">Display Name</span>
                {!isEditingName ? (
                  <button type="button" className="card-edit-btn" onClick={() => { setIsEditingName(true); setTempName(userName); }}>
                    <Edit2 size={12} /> Edit
                  </button>
                ) : (
                  <button type="button" className="card-edit-btn save" onClick={handleSaveName}>
                    <Save size={12} /> Save
                  </button>
                )}
              </div>

              {!isEditingName ? (
                <span className="card-value">{userName}</span>
              ) : (
                <input
                  type="text"
                  className="card-value-input"
                  value={tempName}
                  onChange={(e) => setTempName(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && handleSaveName()}
                  placeholder="Enter your name"
                  autoFocus
                />
              )}
            </div>

            {/* Email Address Card */}
            <div className="partner-modal-card">
              <div className="card-label-row">
                <span className="card-label">Email Address</span>
                {!isEditingEmail ? (
                  <button type="button" className="card-edit-btn" onClick={() => { setIsEditingEmail(true); setTempEmail(userEmail); }}>
                    <Edit2 size={12} /> Edit
                  </button>
                ) : (
                  <button type="button" className="card-edit-btn save" onClick={handleSaveEmail}>
                    <Save size={12} /> Save
                  </button>
                )}
              </div>

              {!isEditingEmail ? (
                <span className="card-value">{userEmail || "Not specified"}</span>
              ) : (
                <input
                  type="email"
                  className="card-value-input"
                  value={tempEmail}
                  onChange={(e) => setTempEmail(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && handleSaveEmail()}
                  placeholder="Enter email address"
                  autoFocus
                />
              )}
            </div>

            {/* Organization Card */}
            <div className="partner-modal-card" style={{ gridColumn: "1 / -1" }}>
              <div className="card-label-row">
                <span className="card-label">Organization</span>
                {!isEditingOrg ? (
                  <button type="button" className="card-edit-btn" onClick={() => { setIsEditingOrg(true); setTempOrgName(orgName); }}>
                    <Edit2 size={12} /> Edit
                  </button>
                ) : (
                  <button type="button" className="card-edit-btn save" onClick={handleSaveOrgName}>
                    <Save size={12} /> Save
                  </button>
                )}
              </div>

              {!isEditingOrg ? (
                <span className="card-value">{orgName}</span>
              ) : (
                <input
                  type="text"
                  className="card-value-input"
                  value={tempOrgName}
                  onChange={(e) => setTempOrgName(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && handleSaveOrgName()}
                  autoFocus
                />
              )}
            </div>
          </div>

          <div className="partner-modal-footer-actions">
            <button
              type="button"
              className="partner-modal-close-btn"
              onClick={() => setShowAccountModal(false)}
            >
              Close Settings
            </button>
          </div>
        </div>
      </Modal>
    </header>
  );
};

export default Header;
